import React, { useState, useEffect } from 'react'
import { Table, Row, Col } from 'react-bootstrap'
import { Button } from 'antd'
import { ExerciseStatus } from '../../../constants/exercises'
import ExerciseIntro from '../../common/components/ExerciseIntro'
import FooterButtons from '../../common/components/FooterButtons'
import RnProgress from './RnProgress'
import ExerciseComplete from '../../common/components/ExerciseComplete'
import { getCenteredBreathingText, getCognitiveFooterButtons, getTotalMinutes, isReadyToStart } from '../../common/exerciseHelper'
import { RnExercise, RnUserAnswer } from '../rnSlice'
import { getResults, getCurrent, getChoiceLabel } from '../rnHelper'
import RightWrongIcon from '../../common/components/RightWrongIcon'
import { pages } from '../../../constants/pages'
import { Question, QuestionText, Choice, ChoiceText } from './commonStyles'
import classnames from 'classnames'
import RulesKeyModal, { ModalType } from './RulesKeyModal'

export interface IRnExercisesProps {
  status: ExerciseStatus
  exercises: RnExercise[]
  userAnswers: RnUserAnswer[]
  startTime?: number
  endTime?: number
  submitAnswer: (answer: number) => void
  next: (repeat?: boolean) => void
  start: (level: number) => void
  backTo: (route: string) => void
}

const RnExercises = ({ status, exercises, userAnswers, startTime, endTime, start, next, submitAnswer, backTo }: IRnExercisesProps) => {
  const [answer, setAnswer] = useState<number | undefined>(undefined)
  const [modalType, setModalType] = useState<ModalType | undefined>(undefined)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleNext = () => {
    window.scrollTo(0, 0)
    next()
  }

  const handleRepeat = () => {
    window.scrollTo(0, 0)
    next(true)
  }

  const handleSubmitAnswer = () => {
    if (answer !== undefined) {
      submitAnswer(answer)
      setAnswer(undefined)
      window.scrollTo(0, 0)
    }
  }

  const handleStart = (level: number) => {
    window.scrollTo(0, 0)
    start(level)
  }

  const { currentExerciseNumber, currentSet, currentExercise, currentExerciseSet } = getCurrent(exercises, userAnswers)

  const { level } = currentExercise || {}

  const totalMinutes = getTotalMinutes(startTime, endTime)

  const { maxSet, results } = getResults(status, exercises, userAnswers)

  const completedExercises = currentExerciseNumber - 1

  const answers = ['4', '11', '6']
  const answerCols = answers.map((answerText, index) => {
    const buttonClasses = classnames('pt-3', { selected: index === 2 })
    return (
      <Col xs={10} sm={8} md={4} key={`answer-${index}`} className="my-1">
        <Choice className={buttonClasses} block>
          <ChoiceText>{answerText}</ChoiceText>
          <h6>{getChoiceLabel(index)}</h6>
        </Choice>
      </Col>
    )
  })
  

  return (
    <>
      <ExerciseIntro status={status}>
        <h4>Instructions</h4>
        <p>Answer each question by finding the actual number that the Roman numeral represents or by finding which Roman numeral represents the actual number.  Try to do this in your mind’s eye first before answering.</p>
        <p>Use the <Button onClick={() => setModalType('key')}>key</Button> and <Button onClick={() => setModalType('rules')}>rules</Button> if you need help.</p>
        <h5>Example</h5>
        <Row className="mb-3 justify-content-center">
          <Col xs={12}> 
            <Question className="text-center">
              <h5 className="text-left">{`Set 1`}</h5>
              <QuestionText count={2}>VI</QuestionText>
            </Question>
          </Col>
        </Row>
        <Row className="justify-content-center">
          {answerCols}
        </Row>
        <p>In this example, the correct answer is C “6”.</p>
        <RulesKeyModal type={modalType} close={() => setModalType(undefined)} />
      </ExerciseIntro>
      <RnProgress 
        status={status}
        exercises={exercises}
        level={level}
        currentExercise={currentExerciseNumber}
        currentSet={currentSet}
        currentExerciseSet={currentExerciseSet}
        answer={answer}
        pickAnswer={(choice: number) => setAnswer(choice)}
        next={handleSubmitAnswer}
        start={handleStart}
      />
      <ExerciseComplete status={status} category="cognitive" totalMinutes={totalMinutes} completedExercises={completedExercises}>
        <h4>Result</h4>
        <Table striped responsive>
          <thead>
            <tr>
              <th key="empty">&nbsp;</th>
              <th align="center">Level</th>
              { Array.from(Array(maxSet).keys()).map(index => (<th align="center" key={index}>Set {index + 1}</th>)) }
              <th align="center" key="correct">Correct %</th>
            </tr>            
          </thead>
          <tbody>
            { results.map((result, i) => (
              <tr key={`row-${i}`}>
                <td key="ex">Ex.&nbsp;{results.length - i}</td>
                <td align="center">{result.level}</td>
                { result.sets.map((set, j) => (<td align="center" key={`cell-${i}-${j}`}><RightWrongIcon correct={set} /></td>))}
                <td align="center" key="percentage">{result.percentage}%</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ExerciseComplete>
      <FooterButtons
        extraText={getCenteredBreathingText(status)}
        hidden={status === 'in-progress'}
        buttons={getCognitiveFooterButtons(
          status,
          pages.romanNumerals.title!,
          isReadyToStart(status, exercises),
          backTo,
          handleNext,
          handleRepeat,
          "Go to the next exercise",
          "Select a different level",
        )}
      />  
    </>
  )
}

export default RnExercises